// SPDX-License-Identifier: EUPL-1.2
// Copyright (C) 2026 Conduction B.V.
//
// Shared state for the flow editor.
//
// The canvas (CnFlowDetail) and the controls (CnFlowSidebar) render in two
// different places — the page body and Nextcloud's app sidebar — so they cannot
// pass props to one another. The flow being edited lives here instead.
//
// Ported from hermiq's `graphEditor` store, which was the fleet's only flow
// editor. Two things changed in the port:
//
//   1. Persistence is OpenRegister's ONE flow store (`/api/flows`), not an app's
//      own register. That is the whole point of the unification: hermiq kept
//      flows as `agentflow` objects, so every app that wanted flows needed its
//      own register, its own resolver and — in practice — its own executor.
//   2. Node types are the catalogue's ids, VERBATIM. hermiq's builder fed its
//      palette from the catalogue (namespaced: `openregister.set-fields`,
//      `hermiq.agent-step`) while its config panes and its executor matched
//      BARE ids (`set-fields`, `agent-step`). Every node placed from the palette
//      therefore had no config pane and was skipped at run time — silently, with
//      the run reporting success. Nothing here may reintroduce a second
//      vocabulary.

import axios from '@nextcloud/axios'
import { generateUrl } from '@nextcloud/router'
import { defineStore } from 'pinia'
import { layoutFlowNodes, needsFullLayout, placeLooseNodes } from './flowGraphLayout.js'
import { DEFAULT_EDGE_LINE_TYPE } from './useFlowEdgeStyles.js'

/**
 * A blank flow definition.
 *
 * @return {object} The empty flow.
 */
/**
 * The fields that make up the pinned DEFINITION of a flow.
 *
 * Kept in step with `FlowDefinitionPin::PINNED_KEYS` on the server: these four
 * are what a version's hash is taken over, so these four are what a published
 * version refuses to have changed.
 *
 * @type {string[]}
 */
const GRAPH_FIELDS = ['nodes', 'edges', 'limits', 'executionMode']

/**
 * The run statuses the engine treats as "still going".
 *
 * Mirrors `FlowRun::ACTIVE` on the server (`queued`, `running`, `suspended`);
 * everything else (`completed`, `stopped`, `failed`, `dead_letter`) is
 * terminal. The watcher polls while a run is in this set and stops by itself
 * the moment it leaves it.
 *
 * @type {string[]}
 */
export const FLOW_RUN_ACTIVE_STATUSES = ['queued', 'running', 'suspended']

/**
 * How often the watched run is polled, and the floor under a caller-supplied
 * interval.
 *
 * 3s because the editor's Run is interactive — a person is looking — so the
 * dashboard widget's 15s feels dead; floored at 2s because the engine writes
 * the log once per worker pass, so polling faster multiplies requests for no
 * extra information.
 */
const WATCH_INTERVAL_DEFAULT_MS = 3000
const WATCH_INTERVAL_FLOOR_MS = 2000

function emptyFlow() {
	return {
		name: '',
		description: '',
		notes: '',
		app: 'openregister',
		triggerRegister: '',
		triggerSchema: '',
		trigger: 'object.updated',
		cron: '',
		executionMode: 'async',
		enabled: false,
		nodes: [],
		edges: [],
		limits: {},
		retentionDays: null,
		auditEnabled: null,
		oversightEnabled: null,

		// A new flow is a DRAFT. Nothing runs it until it is published, which
		// is the point: an author builds a graph over several sittings and a
		// half-wired process must not be firing on object writes in between.
		version: 1,
		lifecycleStatus: 'draft',
	}
}

export const useFlowStore = defineStore('cnFlow', {
	state: () => ({
		flow: emptyFlow(),
		flows: [],

		// Snapshots of `flow`, oldest first, for Ctrl+Z.
		//
		// A canvas is direct manipulation: a mis-drag or a mis-aimed Delete
		// destroys work with no dialog in between, and until this existed the
		// only way back was to reload and lose everything since the last save.
		// The stack is capped — see pushUndo() — because a flow document is
		// held whole in each entry.
		undoStack: [],
		selectedNodeId: null,
		paletteDragType: null,

		// The node whose edit dialog is open, or null. Lives here because the
		// dialog is hosted by the canvas while it is opened from the sidebar,
		// a node double-click, or a consumer.
		editingNodeId: null,

		// The CONNECTION whose edit dialog is open, as `{source, target}`, or
		// null.
		//
		// ⚠️ NOT AN ID. `canvasEdges` expands one stored edge into several drawn
		// lines — `{from: 'a', to: ['b', 'c']}` is two lines — so a line's id is
		// synthesised per render and means nothing to the document. The endpoint
		// pair is the only thing that identifies a line in both worlds, and it
		// is what every edge action below is keyed on.
		editingEdge: null,

		// The label and router copied off a line, for pasting onto another. A
		// line has no meaningful duplicate — two identical `from`/`to` records
		// draw on top of each other and the engine dedupes them — so Copy on a
		// connection copies its PRESENTATION, which is the part that is actually
		// worth repeating across a graph.
		edgeStyleClipboard: null,

		// Whether the controls sidebar is shown. Lives here because the
		// re-open affordance sits on the canvas toolbar, in the other half of
		// the tree.
		sidebarOpen: true,

		// Every version of the open flow, newest first.
		versions: [],

		// The server's last lifecycle refusal, as `{reason, lifecycleStatus}`.
		//
		// 🔑 KEPT AS THE SERVER'S FIELDS, not as a rendered sentence. "this
		// version is published, create a draft" and "this flow has no published
		// version, publish one" want opposite buttons, and choosing between
		// them by matching on message text is how a UI offers the wrong one.
		lifecycleRefusal: null,

		// Whether a lifecycle transition is in flight.
		transitioning: false,

		// What publishing WOULD do, as the server answered it: the next
		// semantic version, and when major, what the publish takes away.
		//
		// 🔑 THE SERVER'S ANSWER, NOT A SECOND OPINION. Deriving "this looks
		// breaking" in the client would be a second implementation of the rule,
		// and the first time the two disagree the author learns to believe
		// neither.
		publishPreview: null,

		// Whether that preview is being fetched.
		previewingPublish: false,

		// The server's refusal of an explicit bump, as a sentence naming what
		// was removed. Separate from `lifecycleRefusal`: the flow's state is
		// fine, the REQUEST is what was wrong.
		versionBumpRefusal: null,

		// The node types the engine can actually execute. The catalogue is
		// AUTHORITATIVE: a builder that invents its own ids produces flows the
		// engine cannot run, which is exactly the defect this replaces.
		nodeCatalog: [],
		eventCatalog: [],

		// Whether the catalogue request is still in flight. Without this flag
		// the palette cannot tell "loading" from "failed": both are an empty
		// list, and the sidebar showed "Could not read the flow engine's node
		// types" on every fresh /flows/new while the request was mid-air.
		catalogLoading: false,

		// The runs of the flow being edited, and the per-node steps of the run
		// currently being inspected.
		runs: [],
		steps: [],
		// What the inspected run TOUCHED, grouped by node. Distinct from
		// `steps`, which is what it did: a step that wrote nothing still has a
		// step row, and an object written by an app the node called into has no
		// step of its own.
		runObjects: [],
		// The tasks THIS RUN raised, from `GET /api/flow-tasks?runUuid={uuid}`.
		//
		// The parameter ANCHORS the read rather than filtering it: with it
		// present the server ignores `scope`, so these are the run's tasks and
		// not the reader's inbox. Visibility still applies on top, so a caller
		// who is neither the run's requester nor an admin can see fewer rows
		// than the run has. That is correct, and nothing here works around it.
		runTasks: [],
		inspectedRunUuid: null,

		// THE INSPECTED RUN'S OWN RECORD, as the server answered it.
		//
		// `runs` is the flow's run history and it is capped at 25, so a run
		// reached by `?run=` need not be in it at all. Reading the header from
		// that list alone left a deep-linked run with no status, no time and no
		// error, which reads as a run that recorded nothing.
		//
		// It also carries what `GET /flow-runs/{uuid}/objects` cannot: the
		// objects the run is ABOUT (`subjects`) and the ones it is holding at a
		// place (`placeItems`). That endpoint reports audited CHANGES, so a run
		// that waited on a locked object and failed lists nothing there while
		// its own error names that object.
		runDetail: null,

		// THE GRAPH AS IT RAN, not the graph as it is now.
		//
		// A run records the `flowVersion` it executed, and a flow's graph moves
		// on: steps are added, renamed and deleted. Replaying an old run over
		// today's canvas paints badges onto nodes that were not there when it
		// ran and silently drops the ones that have since been deleted — the
		// `run-skipped` warning in CnFlowDetail exists solely to apologise for
		// that. So inspecting a run loads that version's stored graph and shows
		// it, and the reader is looking at what actually executed.
		//
		// `viewingVersion` is the version number on the canvas, or null for the
		// live graph. `liveGraph` is the graph that was displaced, kept so
		// closing the run restores it EXACTLY rather than refetching and hoping.
		// `runGraphNotice` is why the snapshot is NOT on the canvas when a run
		// is open, as `{ version, reason }`. There are two reasons and they are
		// not interchangeable: `unreadable` is a version row that could not be
		// fetched, and `unsaved-edits` is a canvas we refused to displace. Both
		// end with the replay drawn over the live graph, so without this the
		// reader cannot tell which graph they are looking at, and the answer
		// differs from the one the `viewingVersion` banner would have given.
		viewingVersion: null,
		liveGraph: null,
		runGraphNotice: null,

		// The run being WATCHED live: the one `run()` just queued, polled while
		// its status is non-terminal. The store exposes FACTS about it — the
		// run, its ordered steps, which of them are new since the last poll —
		// and animates nothing: the animation timeline lives in CnFlowDetail,
		// the only place that knows what a hop looks like. Splitting it this
		// way keeps the store testable without a clock.
		watchedRunUuid: null,
		watchedRun: null,

		// The watched run's log so far, in the engine's order. WITHIN ONE RUN
		// THE LOG ONLY GROWS — the engine appends per worker pass, and a
		// resumed pass appends after the `suspended` entry — so entries are
		// appended by index and an already-exposed step is never replaced or
		// replayed.
		watchedSteps: [],

		// Where the latest poll's NEW entries start in `watchedSteps`. The
		// index diff is exact because the log only grows; a consumer reads
		// `newWatchedSteps` to learn what arrived without replaying the rest.
		watchedNewFrom: 0,

		// Whether the poll loop is live. Stays true while the tab is hidden —
		// the timer is paused, not the watch — and turns false on a terminal
		// status, a re-watch, or teardown.
		watching: false,

		// A replay request for the INSPECTED run, as a monotonic token. The
		// sidebar raises it (`requestReplay()`), the canvas half watches it and
		// plays `steps` through its animator. A token rather than a flag so
		// replaying the same run twice in a row is two requests.
		replayUuid: null,
		replayToken: 0,

		// The flow LIST request, and nothing else. Read the comment below before
		// reaching for it as "the editor is loading": it is not.
		loading: false,
		saving: false,
		running: false,
		checking: false,

		// 🔴 WHAT `loading` DOES NOT COVER, AND WHY A SECOND FLAG EXISTS.
		//
		// `loading` is set around `GET /api/flows` alone. The editor's arrival is
		// up to three requests deep: the list, then the run named by `?run=`,
		// then the stored graph that run executed. The canvas rendered
		// "No steps yet" through all of it, because `nodes.length === 0` is true
		// for a flow that has not arrived yet.
		//
		// An empty state that means "still fetching" is indistinguishable from
		// one that means "this flow has no steps", and only the second is an
		// answer. So `bootstrapping` spans the WHOLE arrival: the surface about
		// to make those requests raises it before the first one, and lowers it
		// when the last has settled.
		bootstrapping: false,

		// The run the URL named, from before it has been fetched.
		//
		// `inspectedRunUuid` is only set once `inspectRun()` runs, and that is
		// behind the flow load, so a visitor arriving on `/flows/x?run=y` saw
		// the full flow editor first and the run view a moment later, complete
		// with Add a step, Save and a sidebar telling them to save the flow.
		// The URL had already said a run was being viewed, so this records that
		// fact synchronously and `inRunView` reads both.
		openingRunUuid: null,

		// The engine's verdict on the unsaved canvas, from `check()`. Cleared
		// by any edit that could change it, so a stale "looks runnable" never
		// outlives the graph it described.
		checkResult: null,

		dirty: false,
		error: null,
		/**
		 * The flow id that could not be resolved, or null.
		 *
		 * Distinct from `error`, which is a failed REQUEST. A flow that resolves
		 * to nothing is a successful request with an empty answer, and the canvas
		 * must be able to tell a reader that rather than rendering an empty grid
		 * that looks like a flow with no steps.
		 */
		notFound: null,
	}),

	getters: {
		nodes: (state) => state.flow.nodes || [],
		edges: (state) => state.flow.edges || [],

		/**
		 * Whether this flow runs inline rather than through the worker.
		 *
		 * The engine's own vocabulary is `sync` / `async` on the flow, and the
		 * default is async — so anything the API has not told us about, or has
		 * not set, queues. Reading it here rather than at the call site keeps
		 * the one place that knows the vocabulary.
		 *
		 * @param {object} state The store state.
		 * @return {boolean} True when a run should be executed immediately.
		 */
		runsSynchronously: (state) => String(state.flow.executionMode || '').toLowerCase() === 'sync',

		/**
		 * @param {object} state The store state.
		 * @return {string} The open flow's lifecycle status.
		 */
		lifecycleStatus: (state) => state.flow.lifecycleStatus || 'draft',

		/**
		 * @param {object} state The store state.
		 * @return {number} The open flow's version number.
		 */
		flowVersion: (state) => Number(state.flow.version || 1),

		/**
		 * What the version pill says: the semantic version where the engine
		 * has derived one, the ordinal where it has not.
		 *
		 * A flow that has never been published has no semantic version, and a
		 * draft has none either — it has not been compared with anything yet.
		 * Showing `v0.0.0` there would be a claim; showing the ordinal is the
		 * truth the flow actually carries.
		 *
		 * @param {object} state The store state.
		 * @return {string} The label, without a leading `v`.
		 */
		flowVersionLabel: (state) => {
			const semver = String(state.flow.semver || '').trim()

			return (semver === '') ? String(Number(state.flow.version || 1)) : semver
		},

		/**
		 * @param {object} state The store state.
		 * @return {boolean} Whether the open flow is a draft.
		 */
		isDraft: (state) => (state.flow.lifecycleStatus || 'draft') === 'draft',

		/**
		 * @param {object} state The store state.
		 * @return {boolean} Whether the open flow is published.
		 */
		isPublished: (state) => state.flow.lifecycleStatus === 'published',

		/**
		 * Whether the GRAPH may not be edited.
		 *
		 * 🔴 THE GRAPH, NOT THE FLOW. A published version's nodes and edges are
		 * immutable, but its name, description and enabled flag are not —
		 * renaming a live process or switching it off are not changes to the
		 * process. The server draws the line in exactly the same place, so an
		 * editor that locked everything would refuse edits the API accepts.
		 *
		 * 🔴 A VERSION SNAPSHOT IS LOCKED TOO, AND THIS IS NOT COSMETIC.
		 *
		 * `viewingVersion` means the canvas holds a HISTORIC graph loaded for a
		 * run replay, while `flow.id` still names the live flow. `save()` picks
		 * PUT over POST from `flow.id`, so a single edit-and-save on a snapshot
		 * would overwrite the current flow with an old graph — the same class of
		 * loss the `id` watcher in CnFlowDetail was written to prevent, reached
		 * by a different door. Reusing the existing lock rather than inventing a
		 * read-only mode means every caller that already respects `graphLocked`
		 * — `pushUndo()` and everything gated behind it — is correct here for
		 * free, and a new call site cannot forget the snapshot case.
		 *
		 * @param {object} state The store state.
		 * @return {boolean} Whether graph edits are locked.
		 */
		graphLocked: (state) => state.viewingVersion !== null
			|| ['published', 'deprecated'].includes(state.flow.lifecycleStatus),

		/**
		 * Whether the surfaces are showing a RUN rather than the flow.
		 *
		 * 🔑 TWO SOURCES, AND THE EARLY ONE IS THE POINT. `inspectedRunUuid` is
		 * the run that has been read; `openingRunUuid` is the run the URL named,
		 * known before anything is fetched. Reading only the first meant the
		 * editor spent the whole load in flow-edit mode with the address bar
		 * already saying otherwise, so the reader was offered Add a step, Save
		 * and Run on a page that was about to become read-only.
		 *
		 * @param {object} state The store state.
		 * @return {boolean} True while a run is open or being opened.
		 */
		inRunView: (state) => Boolean(state.inspectedRunUuid) || state.openingRunUuid !== null,

		/**
		 * Whether the canvas is still waiting for the graph it is going to show.
		 *
		 * The one flag a canvas should ask, rather than assembling it from
		 * `loading` plus two run flags at every call site: any of the three
		 * requests behind the first paint being in the air means the graph on
		 * screen is not yet an answer.
		 *
		 * @param {object} state The store state.
		 * @return {boolean} True while the graph is still being fetched.
		 */
		canvasLoading: (state) => state.bootstrapping === true || state.openingRunUuid !== null,

		/**
		 * Whether there is anything to undo. Drives the toolbar's disabled state.
		 *
		 * @param {object} state The store state.
		 * @return {boolean} True when the stack holds at least one snapshot.
		 */
		canUndo: (state) => state.undoStack.length > 0,

		selectedNode: (state) => {
			if (state.selectedNodeId === null) {
				return null
			}

			return (state.flow.nodes || []).find((n) => n.id === state.selectedNodeId) || null
		},

		/**
		 * The node whose edit dialog is open, or null.
		 *
		 * @param {object} state The store state.
		 * @return {object|null} The node.
		 */
		editingNode: (state) => {
			if (state.editingNodeId === null) {
				return null
			}

			return (state.flow.nodes || []).find((n) => n.id === state.editingNodeId) || null
		},

		/**
		 * The catalogue entry for a node type, or null when the engine does not
		 * know it.
		 *
		 * A null here is meaningful and must be SHOWN, not hidden: it means the
		 * flow carries a node the engine cannot resolve, which will fail its
		 * step at run time.
		 *
		 * @param {object} state The store state.
		 * @return {(type: string) => object|null} The catalogue entry, or null when the engine does not know the type.
		 */
		catalogEntry: (state) => (type) => {
			return state.nodeCatalog.find((entry) => (
				entry.id === type || (entry.aliases || []).includes(type)
			)) || null
		},

		/**
		 * A node type's role — `trigger`, `step` or `end` — from the catalogue.
		 *
		 * Falls back to the id's naming convention only while the catalogue has
		 * not loaded, so the canvas is not colourless during the first paint.
		 *
		 * @return {(type: string) => 'trigger'|'step'|'end'} The role of a node type.
		 */
		roleOfNodeType() {
			return (type) => {
				const entry = this.catalogEntry(type)
				if (entry?.role) {
					return entry.role
				}

				const id = String(type || '')
				if (id.includes('.trigger-')) {
					return 'trigger'
				}
				if (id.endsWith('.end') || id.endsWith('.stop')) {
					return 'end'
				}

				return 'step'
			}
		},

		/**
		 * The edges as the canvas draws them: one line per (source, target)
		 * pair, whatever dialect the stored flow speaks.
		 *
		 * This store writes `{from, to}` — the ONLY dialect the engine reads —
		 * but it must still DRAW `{source, target}`, which older documents and
		 * hermiq's flows carry. Each endpoint
		 * may be a LIST (several `from` = join, several `to` = split). Handing
		 * `flow.edges` to the canvas raw therefore rendered every one of those
		 * flows as unconnected cards: real graphs, silently drawn wrong.
		 *
		 * @param {object} state The store state.
		 * @return {Array<object>} Drawable `{id, source, target, edge}` lines.
		 */
		canvasEdges: (state) => {
			const toList = (value) => {
				if (Array.isArray(value)) {
					return value.filter((v) => v !== null && v !== undefined && v !== '')
				}

				return (value === null || value === undefined || value === '') ? [] : [value]
			}

			const lines = []
			for (const [index, edge] of (state.flow.edges || []).entries()) {
				const sources = toList(edge.source ?? edge.from)
				const targets = toList(edge.target ?? edge.to)
				const edgeId = edge.id || `edge-${index}`

				for (const source of sources) {
					for (const target of targets) {
						// The line id must not be the edge id: a split renders
						// one edge as several lines, and v-for keys collide.
						lines.push({
							id: `${edgeId}:${source}:${target}`,
							source,
							target,

							// LET VUE FLOW ROUTE THE LINE.
							//
							// These carried no `type`, so every line fell back
							// to Vue Flow's default bezier — which crosses
							// nodes and doubles back on itself as soon as a
							// graph stops being a straight chain. That read as
							// "our lines are disorderly", and the instinct was
							// to lay the graph out ourselves to compensate.
							//
							// `smoothstep` is orthogonal routing with rounded
							// corners: it is what the hand-drawn canvas was
							// imitating, and the flow-builder's own dead `#edge`
							// slot described the goal as "orthogonal routing
							// plus an explicit arrowhead". Both are Vue Flow
							// options we had simply never set.
							//
							// PER EDGE, and the document wins. `edge.lineType`
							// is stored on the connection, so a single awkward
							// line can be switched to `step`, `straight` or
							// `default` without moving a node — the seam an
							// edge-level control hangs off.
							//
							// ⚠️ THE ROUTER TRAVELS IN `data`, NOT IN `type`.
							// It sat in `type` when the router was the only
							// thing an edge carried, and that quietly ruled out
							// everything else: Vue Flow reads `type` to choose
							// the COMPONENT that draws the line, so naming a
							// router there means the built-in edge answers and
							// no label, marker control or payload affordance
							// can ever be attached. `default` selects
							// CnFlowEdge, which reads the router back out of
							// `data`. Same distinction the node side already
							// records — `type` is a component, and a domain
							// value put there is a component that does not
							// exist.
							type: 'default',

							// ⚠️ SIZED, BECAUSE THE DEFAULT ARROWHEAD WAS DRAWN
							// UNDERNEATH THE PORT HANDLE.
							//
							// `'arrowclosed'` on its own renders a 12.5px arrow
							// at the path's end — which is exactly where the
							// target node's handle sits, and that handle is an
							// 18px filled circle in a layer Vue Flow paints
							// ABOVE the edges. So the arrow was there the whole
							// time, measurably, and no user could see it: the
							// canvas read as a set of undirected lines.
							//
							// 22px is chosen against the handle, not for looks
							// — the arrow has to extend beyond it to read as an
							// arrow at all. Colour is deliberately NOT set here:
							// Vue Flow writes it as an inline style on the
							// polyline, so it is themed in CSS instead (see
							// `.vue-flow__arrowhead` in CnGraphCanvas) and
							// follows dark mode.
							markerEnd: edge.markerEnd || { type: 'arrowclosed', width: 22, height: 22 },
							data: {
								lineType: edge.lineType || DEFAULT_EDGE_LINE_TYPE,
								labelT: edge.labelT,
								label: edge.title || edge.label || '',
								edge,
							},
						})
					}
				}
			}

			return lines
		},

		/**
		 * The nodes a run enters through, decided the way the engine decides.
		 *
		 * @return {Array<string>} Their ids.
		 */
		startNodeIds() {
			const nodes = this.nodes
			const explicit = nodes.filter((n) => n.start === true || n.initial === true).map((n) => n.id)
			if (explicit.length) {
				return explicit
			}

			const targeted = new Set(this.canvasEdges.map((line) => line.target))
			const sources = nodes.filter((n) => !targeted.has(n.id)).map((n) => n.id)
			if (sources.length) {
				return sources
			}

			return nodes.length ? [nodes[0].id] : []
		},

		/**
		 * What stops this flow from ever finishing, by node ROLE.
		 *
		 * An empty flow reports nothing — there is no point telling the author
		 * a blank canvas is incomplete.
		 *
		 * @return {{trigger: boolean, end: boolean}} The missing roles.
		 */
		missingEnds() {
			if (!this.nodes.length) {
				return { trigger: false, end: false }
			}

			const roles = this.nodes.map((n) => this.roleOfNodeType(n.type))

			return {
				trigger: !roles.includes('trigger'),
				end: !roles.includes('end'),
			}
		},

		/**
		 * Whether the watched run is still going.
		 *
		 * Read from the run's own `status` against the engine's non-terminal
		 * set, never from `watching` — the poll loop can be paused (hidden
		 * tab) or already stopped (terminal status seen) while the last
		 * fetched state is what the canvas should keep painting.
		 *
		 * @param {object} state The store state.
		 * @return {boolean} True while the run's status is non-terminal.
		 */
		watchedRunActive: (state) => FLOW_RUN_ACTIVE_STATUSES.includes(state.watchedRun?.status),

		/**
		 * The steps the latest poll ADDED, in log order.
		 *
		 * Index-diffed against what was already exposed, so a consumer can
		 * tell new steps from ones it has already animated. Exact because the
		 * log only grows within a run — see `watchedSteps`.
		 *
		 * @param {object} state The store state.
		 * @return {Array<object>} The engine's log entries, as delivered.
		 */
		newWatchedSteps: (state) => state.watchedSteps.slice(state.watchedNewFrom),

		/**
		 * The flow as the engine would run it.
		 *
		 * @param {object} state The store state.
		 * @return {object} The runnable document.
		 */
		flowForRun: (state) => ({
			...state.flow,
			nodes: state.flow.nodes || [],
			edges: state.flow.edges || [],
		}),
	},

	actions: {
		/**
		 * Declare that the editor is opening, before the first request goes out.
		 *
		 * 🔴 CALLED FROM `created()`, NOT FROM `load()`, AND THAT IS THE FIX.
		 * A flag raised inside `load()` is raised one tick too late: Vue renders
		 * the component before `mounted()` runs, so the first paint had
		 * `bootstrapping === false` with nothing loaded, the exact frame that
		 * showed the editor's toolbar over "No steps yet". The surface that is
		 * about to load says so before it renders.
		 *
		 * @param {string|null} runUuid The run the URL named, if it named one.
		 * @return {void}
		 */
		beginBootstrap(runUuid = null) {
			this.bootstrapping = true
			this.openingRunUuid = runUuid || null
		},

		/**
		 * The arrival has settled: the flow, and the run if there was one, are
		 * as loaded as they are going to get.
		 *
		 * Lowers both flags. A failed load lands here too, via a `finally` at
		 * the call site, because a canvas stuck saying "loading" forever is a
		 * worse lie than the one this replaced.
		 *
		 * @return {void}
		 */
		endBootstrap() {
			this.bootstrapping = false
			this.openingRunUuid = null
		},

		/**
		 * Enter run view for a run that has not been read yet.
		 *
		 * Separate from `beginBootstrap` because a run can also be opened on a
		 * flow that is already on the canvas: `?run=` changing on the same route
		 * reuses the component, so there is no fresh arrival to hang it on.
		 *
		 * @param {string} runUuid The run being opened.
		 * @return {void}
		 */
		beginOpeningRun(runUuid) {
			if (!runUuid) {
				return
			}

			this.openingRunUuid = runUuid
		},

		/**
		 * The run has been read, or the attempt failed. Either way it is no
		 * longer being opened, and `inspectedRunUuid` is what holds run view
		 * from here on.
		 *
		 * @return {void}
		 */
		endOpeningRun() {
			this.openingRunUuid = null
		},

		/**
		 * Load the flows this surface is scoped to, plus both catalogues.
		 *
		 * @param {object}      options     Load options.
		 * @param {string|null} options.app Restrict to one owning app id.
		 * @param {string|null} options.id  The flow to open, or 'new'.
		 * @return {Promise<void>}
		 */
		async load({ app = null, id = null } = {}) {
			// A BLANK flow is initialised BEFORE the network, and this ordering
			// is the fix for #607 rather than a tidy-up.
			//
			// `emptyFlow()` has `name: ''`, and only `open('new')` supplies the
			// default name. `open()` used to run at the tail of this method,
			// behind `await GET /api/flows` — a flow LIST that starting a blank
			// flow does not need. Meanwhile the sidebar is already rendered and
			// Save already enabled, because `nodeCatalog` was populated by the
			// index page's own `load()` and this store is a singleton that
			// survives the route change.
			//
			// So there was a window in which the editor invited a Save of a flow
			// with no name, which `FlowController::create()` answers 400 "A flow
			// needs a name." — measured at 9 of 10 attempts against a fresh
			// instance. The same late `open('new')` also reset `this.flow`, so a
			// step placed during the window was wiped, and a save landing after
			// it stored `nodes: []`.
			//
			// Nothing about a blank flow depends on the server, so it is set up
			// first, and NOT re-opened at the tail — re-opening is what threw the
			// user's work away.
			//
			// `id === 'new'` ONLY, never `id === null`. `save()` calls this
			// method as `load({ app })` to refresh the list, and a null id has
			// always meant "reload the list, leave the open flow alone". Treating
			// that as blank would reset the flow immediately after storing it,
			// discarding the id the server had just returned.
			const isBlank = (id === 'new')
			if (isBlank === true) {
				this.open('new', app)
			}

			this.loading = true
			this.error = null
			try {
				const params = app ? { app } : {}
				const response = await axios.get(generateUrl('/apps/openregister/api/flows'), { params })
				this.flows = response.data?.results || []
			} catch (error) {
				// Surfaced, not swallowed: an empty list with no trace of why is
				// indistinguishable from "this instance has no flows".
				// eslint-disable-next-line no-console
				console.error('cn-flow: could not load flows', error)
				this.flows = []
				this.error = error
			} finally {
				this.loading = false
			}

			// Both catalogues are kept OFF the critical path: the canvas is
			// usable while they resolve, and a failure costs the palette and the
			// trigger list rather than the editor.
			this.loadNodeCatalog()
			this.loadEventCatalog()

			// A blank flow was already opened above, before the network. Opening
			// it again here is what used to wipe a step the user had placed in
			// the meantime, so only a STORED flow is opened at this point — it
			// genuinely needs `this.flows`, which the request above just filled.
			if (id !== null && isBlank === false) {
				// 🔴 THE LIST IS NOT THE ONLY PLACE A FLOW CAN LIVE.
				//
				// `open()` resolves a flow by looking it up in `this.flows`, and
				// that list was just fetched scoped to ONE app. A flow belonging
				// to a different app is therefore absent, `open()` finds no match,
				// and — before this — returned silently, leaving the canvas
				// painted with whatever was there before: blank.
				//
				// That is not a hypothetical. A dossiq case ran eighteen flow runs
				// of a flow whose `app` is `openregister`; clicking any of them
				// opened `/flows/<id>` on dossiq's flow page, which loads
				// `?app=dossiq`, and the editor came up empty with no error.
				// Cross-app runs are ordinary — a case is driven by flows from
				// whichever app authored them.
				//
				// `GET /api/flows/{id}` resolves by uuid with no app filter, so
				// one request answers it. Only reached on a miss, so the common
				// case costs nothing.
				await this.ensureFlowLoaded(id)
				this.open(id, app)
			}
		},

		/**
		 * Make sure a flow is present in `this.flows` before `open()` looks for it.
		 *
		 * Fetches the single flow by uuid when the app-scoped list did not carry
		 * it. A 404 is left alone: `open()` reports the not-found state, which is
		 * the honest answer and the one the canvas can render.
		 *
		 * @param {string} id The flow uuid.
		 * @return {Promise<void>}
		 */
		async ensureFlowLoaded(id) {
			if (!id || id === 'new') {
				return
			}
			if (this.flows.some((flow) => String(flow.id) === String(id))) {
				return
			}

			try {
				const response = await axios.get(generateUrl('/apps/openregister/api/flows/' + encodeURIComponent(id)))
				const flow = response.data
				if (flow && flow.id) {
					this.flows = [...this.flows, flow]
				}
			} catch (error) {
				// Surfaced, not swallowed — but not fatal either. `open()` sets
				// the not-found state right after this, and that is what the
				// canvas renders; throwing here would take out the whole page for
				// a flow that simply is not there any more.
				// eslint-disable-next-line no-console
				console.error('cn-flow: could not resolve flow by id', id, error)
			}
		},

		async loadNodeCatalog() {
			this.catalogLoading = true
			try {
				const response = await axios.get(generateUrl('/apps/openregister/api/flow/node-catalog'))
				this.nodeCatalog = response.data?.results || []
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('cn-flow: could not load the node catalogue', error)
				this.nodeCatalog = []
			} finally {
				this.catalogLoading = false
			}
		},

		async loadEventCatalog() {
			try {
				const response = await axios.get(generateUrl('/apps/openregister/api/flow/event-catalog'))
				this.eventCatalog = response.data?.results || []
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('cn-flow: could not load the event catalogue', error)
				this.eventCatalog = []
			}
		},

		/**
		 * Put the flow named by the route onto the canvas.
		 *
		 * @param {string}      id  The flow uuid, or 'new'.
		 * @param {string|null} app The owning app for a new flow.
		 * @return {void}
		 */
		open(id, app = null) {
			this.selectedNodeId = null
			this.editingNodeId = null
			// Cleared with the rest of the per-flow state. A refusal left over
			// from the last flow would tell the author their NEW draft is
			// published and offer to draft it again.
			this.lifecycleRefusal = null
			this.versions = []
			this.runs = []
			this.steps = []
			this.runObjects = []
			this.inspectedRunUuid = null
			this.runDetail = null
			this.checkResult = null

			// A watch is per run and a run is per flow: polling the previous
			// flow's run from the next flow's editor would keep painting a run
			// this canvas cannot show.
			this.stopWatching()
			this.watchedRunUuid = null
			this.watchedRun = null
			this.watchedSteps = []
			this.watchedNewFrom = 0
			this.replayUuid = null

			// Cleared on every open, so a not-found id from the previous route
			// cannot keep a resolvable flow behind an error screen.
			this.notFound = null

			// Cleared with the rest of the per-flow state. A snapshot left over
			// from the previous flow's run would keep THIS flow's canvas locked,
			// and `liveGraph` would hold another flow's nodes ready to be
			// restored over it.
			this.viewingVersion = null
			this.liveGraph = null
			this.runGraphNotice = null

			if (!id || id === 'new') {
				// A new flow is runnable on demand until its author picks a real
				// trigger, so the flow-level trigger and the seeded start node
				// say the same thing.
				this.flow = { ...emptyFlow(), name: 'New flow', app: app || 'openregister', trigger: 'manual' }
				this.seedStartNode()
				this.dirty = false
				return
			}

			const match = this.flows.find((flow) => String(flow.id) === String(id))
			if (!match) {
				// A blank canvas and no explanation is the worst of the three
				// possible answers here. `notFound` lets the host say which id it
				// could not resolve, so "this flow was deleted" and "this page is
				// broken" stop looking identical.
				this.notFound = String(id)
				return
			}

			this.flow = {
				...emptyFlow(),
				...match,
				// Deep-copied so an edit on the canvas does not mutate the list
				// row behind it — otherwise cancelling an edit leaves the list
				// showing changes that were never saved.
				nodes: Array.isArray(match.nodes) ? JSON.parse(JSON.stringify(match.nodes)) : [],
				edges: Array.isArray(match.edges) ? JSON.parse(JSON.stringify(match.edges)) : [],
			}
			this.dirty = false

			// A flow nobody has ever laid out opens as a PILE, not as a graph.
			//
			// Generated and imported flows carry no coordinates at all — a
			// 76-node flow measured on a live instance had 73 nodes with no
			// position — and every one of those lands on the same point. The
			// result is indistinguishable from an empty canvas: one node's
			// worth of pixels, with the rest underneath it.
			//
			// ⚠️ NOT `autoSort()`. This fallback used to call it, and autoSort
			// gates on `pushUndo()`, which refuses when the graph is locked —
			// and a flow imported from `x-openregister-flows` is PUBLISHED the
			// moment it exists (SchemaFlowImportListener publishes on import).
			// So the exact flows that always need this fallback were the ones
			// it silently skipped: dossiq's 18-node imported flow opened as a
			// pile, with the run replay's badges painting an unreadable point.
			// `applyRenderLayout()` computes coordinates without asking to
			// edit, which a rendering fallback never does.
			this.applyRenderLayout()

			this.loadRuns(id)
		},

		/**
		 * Put the one node every flow starts from onto a blank canvas.
		 *
		 * A new flow renders the SAME editor as an existing one, holding only a
		 * starting point — never an empty page that looks like a different
		 * surface. The id is hard-coded rather than read from the catalogue
		 * because the catalogue loads after `open('new')` returns; if an
		 * instance's engine really does not know it, the card wears the
		 * existing "Unknown step" warning instead of failing silently.
		 *
		 * @return {void}
		 */
		seedStartNode() {
			this.flow.nodes = [{
				id: `start-${Date.now().toString(36)}`,
				type: 'openregister.trigger-manual',
				x: 80,
				y: 60,
				config: {},
				start: true,
			}]
		},

		/**
		 * Add a node of `type`, at an explicit canvas point when dropped.
		 *
		 * `type` is the catalogue id verbatim. Default placement stacks a
		 * vertical chain near the left, with a gap about a card high so the
		 * arrowhead and the result badge on the connecting edge both have room.
		 *
		 * @param {string} type The catalogue node id.
		 * @param {number} x    Canvas x (optional).
		 * @param {number} y    Canvas y (optional).
		 * @return {void}
		 */
		/**
		 * Remember the current flow so the next edit can be undone.
		 *
		 * Called at the TOP of every mutating action, before the mutation — an
		 * undo stack that records the state AFTER a change can only ever return
		 * you to where you already are.
		 *
		 * Deep-cloned, not referenced. `flow` is mutated in place by several
		 * actions, so a stored reference would be rewritten by the very edit it
		 * exists to reverse and every entry would collapse to "now".
		 *
		 * Capped: each entry holds a whole flow document, and a canvas gets a
		 * lot of small edits. 50 is far past what anyone reaches for and still
		 * bounded.
		 *
		 * 🔴 IT ALSO ANSWERS WHETHER THE EDIT MAY HAPPEN AT ALL. Every graph
		 * mutation snapshots here first, which makes this the one place the
		 * published-is-immutable rule can be applied without nine call sites
		 * each remembering to. A locked flow gets `false` and the caller
		 * returns before touching the document — the alternative, letting the
		 * canvas edit freely and discovering the refusal on save, loses the
		 * author's work at the worst possible moment.
		 *
		 * @return {boolean} Whether the caller may proceed with its edit.
		 */
		pushUndo() {
			if (this.graphLocked) {
				this.lifecycleRefusal = {
					reason: 'version-immutable',
					lifecycleStatus: this.lifecycleStatus,
				}
				return false
			}

			// The edit is going ahead, so whatever the flow's state last refused
			// is no longer the situation. A refusal that outlives its cause is
			// a message the author learns to ignore, and then ignores the true
			// one too. Cleared HERE for the same reason the lock is checked
			// here: it is the one place every graph mutation passes through.
			this.lifecycleRefusal = null

			this.snapshot()

			return true
		},

		/**
		 * Push one undo entry, and keep the stack capped.
		 *
		 * 🔴 THE ONLY PLACE THAT PUSHES. Metadata edits are exempt from the
		 * published-is-immutable lock but still want undo, and the first version
		 * of that exemption pushed to `undoStack` directly — bypassing the trim
		 * and letting the stack grow past its cap (caught at 60/50 by
		 * `useFlowStoreUndo.spec.js`). A cap enforced in two places is a cap
		 * enforced in one of them.
		 *
		 * @return {void}
		 */
		snapshot() {
			this.undoStack.push(JSON.stringify(this.flow))
			if (this.undoStack.length > 50) {
				this.undoStack.shift()
			}
		},

		/**
		 * Step back one edit.
		 *
		 * Entries that match the current state are DISCARDED rather than
		 * applied. Actions snapshot before they know whether they will change
		 * anything — `connect()` refuses a duplicate, `setNodeConfig()` may be
		 * handed the value already stored — so without this a user could press
		 * Ctrl+Z and watch nothing happen, which reads as "undo is broken"
		 * rather than "that edit was a no-op".
		 *
		 * @return {boolean} Whether anything was undone.
		 */
		undo() {
			const current = JSON.stringify(this.flow)

			while (this.undoStack.length > 0) {
				const previous = this.undoStack.pop()
				if (previous === current) {
					continue
				}

				this.flow = JSON.parse(previous)
				this.dirty = true

				// The verdict described the graph that was just replaced.
				this.checkResult = null

				// A node that no longer exists cannot stay selected, and an open
				// editor for it would render against nothing.
				if (this.nodes.some((node) => node.id === this.selectedNodeId) === false) {
					this.selectedNodeId = null
				}
				if (this.nodes.some((node) => node.id === this.editingNodeId) === false) {
					this.editingNodeId = null
				}

				return true
			}

			return false
		},

		addNode(type, x = null, y = null) {
			if (!this.pushUndo()) {
				return
			}

			const index = this.nodes.length
			const node = {
				id: `${type}-${Date.now().toString(36)}-${index}`,
				type,
				x: x === null ? 80 : x,
				y: y === null ? (60 + index * 170) : y,
				config: {},
			}
			if (index === 0) {
				node.start = true
			}

			this.flow.nodes = [...this.nodes, node]
			this.selectedNodeId = node.id
			this.dirty = true
			this.checkResult = null
		},

		/**
		 * WRITES BOTH SPELLINGS, FOR THE SAME REASON connect() WRITES `from`/`to`.
		 *
		 * `position: {x, y}` is the shape the SERVER stores — measured on a live
		 * instance, not one of 100 persisted flows carried a flat `x`/`y` node.
		 * Writing only flat coordinates therefore meant a dragged node's new
		 * position never survived the save: it round-tripped back as no position
		 * at all, and the node reloaded at the origin.
		 *
		 * Flat `x`/`y` is kept alongside it because autoSort() and addNode()
		 * speak that spelling in memory, and dropping it here would make a moved
		 * node inconsistent with an unmoved one within the same session.
		 *
		 * @param {object} move   The move.
		 * @param {string} move.id The node that moved.
		 * @param {number} move.x  Its new x, in canvas units.
		 * @param {number} move.y  Its new y.
		 * @return {void}
		 */
		/**
		 * Duplicate a step, offset so the copy is visibly its own node.
		 *
		 * The config is deep-cloned. A shallow copy would leave both steps
		 * sharing one config object, so editing the duplicate would silently
		 * rewrite the original — the copy would look independent and not be.
		 *
		 * Edges are deliberately NOT copied. A duplicate wired exactly like its
		 * original would fan the flow in two at that point, which is a different
		 * graph from the one the author asked for; they connect the copy where
		 * they want it.
		 *
		 * `start` is not copied either: a flow has one starting point, and a
		 * second node claiming it makes the entry ambiguous.
		 *
		 * @param {string} id The node to copy.
		 * @return {string|null} The new node's id, or null when there was nothing to copy.
		 */
		copyNode(id) {
			const source = this.nodes.find((node) => node.id === id)
			if (source === undefined) {
				return null
			}

			// `null`, matching the not-found case above: this method's contract
			// is "the new node's id, or null when nothing was copied", and a
			// bare `return` would hand callers `undefined` for one refusal and
			// `null` for the other.
			if (!this.pushUndo()) {
				return null
			}

			const copy = {
				...source,
				id: `${source.type}-${Date.now().toString(36)}-${this.nodes.length}`,
				x: (source.x ?? 0) + 40,
				y: (source.y ?? 0) + 40,
				config: JSON.parse(JSON.stringify(source.config ?? {})),
			}
			copy.position = { x: copy.x, y: copy.y }
			delete copy.start
			delete copy.initial

			this.flow.nodes = [...this.nodes, copy]
			this.selectedNodeId = copy.id
			this.dirty = true
			this.checkResult = null

			return copy.id
		},

		moveNode({ id, x, y }) {
			if (!this.pushUndo()) {
				return
			}

			this.flow.nodes = this.nodes.map((node) => (node.id === id ? { ...node, x, y, position: { x, y } } : node))
			this.dirty = true
		},

		/**
		 * Connect two nodes (no duplicates, no self-edges).
		 *
		 * WRITES `from`/`to`, WHICH IS THE DIALECT THE ENGINE READS.
		 *
		 * This used to store `{source, target}`. The canvas rendered it — the
		 * `canvasEdges` getter accepts either spelling — so the author saw the
		 * connection, and the save succeeded. But OpenRegister's flow engine
		 * only understands `from`/`to`, so every edge drawn in the editor was
		 * INVISIBLE to it: the saved flow validated with a `node-dead-end`
		 * warning and a run was refused outright with "node … has no outgoing
		 * edge and does not end the flow".
		 *
		 * The editor could therefore not produce a runnable multi-node flow at
		 * all, while looking like it had. Measured against a live instance:
		 * `edges: [{source, target}]` returns that warning, `[{from, to}]`
		 * returns clean.
		 *
		 * The duplicate check reads BOTH spellings, so a flow loaded from the
		 * server (which is `from`/`to`) is not re-connected into a duplicate.
		 *
		 * @param {object} payload        The connection.
		 * @param {string} payload.source Source node id.
		 * @param {string} payload.target Target node id.
		 * @return {void}
		 */
		connect({ source, target }) {
			if (!this.pushUndo()) {
				return
			}

			if (!source || !target || source === target) {
				return
			}

			const exists = this.edges.some((edge) => (edge.source ?? edge.from) === source && (edge.target ?? edge.to) === target)
			if (exists) {
				return
			}

			this.flow.edges = [...this.edges, { from: source, to: target }]
			this.dirty = true
			this.checkResult = null
		},

		/**
		 * The stored edge a drawn line came from, and how it is stored.
		 *
		 * Both dialects and both cardinalities: an edge may speak
		 * `{source, target}` or `{from, to}`, and either endpoint may be a LIST
		 * (several `from` is a join, several `to` is a split). So "the edge for
		 * line a→c" is not simply `edges.find(...)` — it may be one entry among
		 * three that the same record draws.
		 *
		 * @param {object} line          The line.
		 * @param {string} line.source   Its source node id.
		 * @param {string} line.target   Its target node id.
		 * @return {{index: number, edge: object, sourceKey: string, targetKey: string, sources: Array<string>, targets: Array<string>}|null}
		 *   Where it lives, or null when no stored edge draws it.
		 */
		locateEdge({ source, target }) {
			const toList = (value) => {
				if (Array.isArray(value)) {
					return value.filter((v) => v !== null && v !== undefined && v !== '')
				}

				return (value === null || value === undefined || value === '') ? [] : [value]
			}

			const edges = this.edges
			for (let index = 0; index < edges.length; index++) {
				const edge = edges[index]
				const sourceKey = edge.source !== undefined ? 'source' : 'from'
				const targetKey = edge.target !== undefined ? 'target' : 'to'
				const sources = toList(edge[sourceKey])
				const targets = toList(edge[targetKey])

				if (sources.includes(source) && targets.includes(target)) {
					return { index, edge, sourceKey, targetKey, sources, targets }
				}
			}

			return null
		},

		/**
		 * Remove ONE drawn connection.
		 *
		 * ⚠️ ONE LINE, NOT ONE RECORD. Deleting a→c out of `{from: 'a', to:
		 * ['b','c']}` must leave a→b standing: dropping the whole record because
		 * it happens to be where a→c is stored would silently delete a second
		 * connection the author never selected. So a multi-line record is
		 * narrowed rather than removed, and only a record left with nothing on
		 * one end goes.
		 *
		 * @param {object} line        The line to remove.
		 * @param {string} line.source Its source node id.
		 * @param {string} line.target Its target node id.
		 * @return {void}
		 */
		removeEdge({ source, target }) {
			const found = this.locateEdge({ source, target })
			if (found === null) {
				return
			}

			if (!this.pushUndo()) {
				return
			}

			const targets = found.targets.filter((id) => id !== target)
			const sources = found.sources.length > 1 ? found.sources.filter((id) => id !== source) : found.sources

			const edges = [...this.edges]
			if (targets.length === 0 || sources.length === 0) {
				edges.splice(found.index, 1)
			} else {
				edges[found.index] = {
					...found.edge,
					[found.sourceKey]: sources.length === 1 ? sources[0] : sources,
					[found.targetKey]: targets.length === 1 ? targets[0] : targets,
				}
			}

			this.flow.edges = edges
			this.dirty = true
			this.checkResult = null
		},

		/**
		 * Set a field — `title`, `lineType` — on ONE drawn connection.
		 *
		 * ⚠️ SPLITS A MULTI-LINE RECORD FIRST. Writing `lineType` straight onto
		 * `{from: 'a', to: ['b','c']}` would restyle both lines from a control
		 * the author opened on one of them. A per-line control whose blast
		 * radius is three lines is not a per-line control, so the line being
		 * edited is lifted into a record of its own and the field is set there.
		 *
		 * Takes a whole set of fields rather than one, so pasting a copied
		 * presentation is a SINGLE undo step. Two `key`/`value` calls in a row
		 * would leave a user pressing Ctrl+Z twice to reverse one paste, and the
		 * first press would land on a half-pasted line.
		 *
		 * @param {object} change        The change.
		 * @param {string} change.source The line's source node id.
		 * @param {string} change.target The line's target node id.
		 * @param {object} change.fields The fields to set, as a plain object.
		 * @return {void}
		 */
		setEdgeFields({ source, target, fields }) {
			const found = this.locateEdge({ source, target })
			if (found === null) {
				return
			}

			if (!this.pushUndo()) {
				return
			}

			const edges = [...this.edges]
			const drawsOneLine = found.sources.length === 1 && found.targets.length === 1

			if (drawsOneLine) {
				edges[found.index] = { ...found.edge, ...fields }
			} else {
				// Narrow the record to the lines this one is NOT, then append the
				// edited line as its own record.
				const remainingTargets = found.targets.filter((id) => id !== target)
				edges[found.index] = {
					...found.edge,
					[found.targetKey]: remainingTargets.length === 1 ? remainingTargets[0] : remainingTargets,
				}
				edges.push({
					...found.edge,
					id: undefined,
					[found.sourceKey]: source,
					[found.targetKey]: target,
					...fields,
				})
			}

			this.flow.edges = edges
			this.dirty = true
			this.checkResult = null
		},

		/**
		 * Copy a connection's presentation, for pasting onto another.
		 *
		 * @param {object} line        The line to copy from.
		 * @param {string} line.source Its source node id.
		 * @param {string} line.target Its target node id.
		 * @return {void}
		 */
		copyEdgeStyle({ source, target }) {
			const found = this.locateEdge({ source, target })
			if (found === null) {
				return
			}

			this.edgeStyleClipboard = {
				title: found.edge.title ?? found.edge.label ?? '',
				lineType: found.edge.lineType || DEFAULT_EDGE_LINE_TYPE,
			}
		},

		/**
		 * Apply the copied presentation to another connection.
		 *
		 * @param {object} line        The line to paste onto.
		 * @param {string} line.source Its source node id.
		 * @param {string} line.target Its target node id.
		 * @return {void}
		 */
		pasteEdgeStyle({ source, target }) {
			const copied = this.edgeStyleClipboard
			if (copied === null) {
				return
			}

			this.setEdgeFields({ source, target, fields: { lineType: copied.lineType, title: copied.title } })
		},

		removeNode(id) {
			if (!this.pushUndo()) {
				return
			}

			this.flow.nodes = this.nodes.filter((node) => node.id !== id)
			// BOTH spellings, or removing a node leaves its `from`/`to` edges
			// behind pointing at a node that no longer exists — and `from`/`to`
			// is exactly what the server stores, so every edge on a loaded flow
			// survived the deletion of the node it referenced.
			this.flow.edges = this.edges.filter((edge) => (edge.source ?? edge.from) !== id && (edge.target ?? edge.to) !== id)
			this.selectedNodeId = null
			// A dialog open on a line that ran through this node now describes
			// a connection the document no longer holds; every edge action would
			// find nothing and quietly do nothing.
			if (this.editingEdge?.source === id || this.editingEdge?.target === id) {
				this.editingEdge = null
			}
			this.dirty = true
			this.checkResult = null
		},

		/**
		 * Replace the selected node's whole config.
		 *
		 * The raw-config editor needs this: it edits the object as a document, so
		 * a per-key setter could never REMOVE a key, and a node type whose keys
		 * the builder does not know would accumulate stale ones.
		 *
		 * @param {object} config The new config object.
		 * @return {void}
		 */
		setNodeConfigAll(config) {
			if (!this.pushUndo()) {
				return
			}

			if (this.selectedNodeId === null) {
				return
			}

			this.flow.nodes = this.nodes.map((node) => (
				node.id === this.selectedNodeId ? { ...node, config: { ...config } } : node
			))
			this.dirty = true
			this.checkResult = null
		},

		setNodeConfig(key, value) {
			if (!this.pushUndo()) {
				return
			}

			if (this.selectedNodeId === null) {
				return
			}

			this.flow.nodes = this.nodes.map((node) => (
				node.id === this.selectedNodeId
					? { ...node, config: { ...(node.config || {}), [key]: value } }
					: node
			))
			this.dirty = true
			this.checkResult = null
		},

		setFlowField(key, value) {
			// 🔴 THE LOCK APPLIES TO THE FOUR GRAPH KEYS AND NO OTHERS, exactly
			// where the server draws the line. `executionMode` and `limits` are
			// part of the pinned definition; `name`, `description` and `enabled`
			// are not — renaming a live process or switching it off are not
			// changes to the process. Locking those too would refuse edits the
			// API accepts and leave a published flow unmanageable.
			if (GRAPH_FIELDS.includes(key)) {
				if (!this.pushUndo()) {
					return
				}
			} else {
				this.snapshot()
			}

			this.flow = { ...this.flow, [key]: value }
			this.dirty = true
		},

		/**
		 * Rename one node. The name is display only — the card's headline —
		 * so it does not invalidate a check verdict.
		 *
		 * @param {string} id   The node id.
		 * @param {string} name The new name; empty falls back to the type label.
		 * @return {void}
		 */
		setNodeName(id, name) {
			if (!this.pushUndo()) {
				return
			}

			this.flow.nodes = this.nodes.map((node) => (
				node.id === id ? { ...node, name } : node
			))
			this.dirty = true
		},

		/**
		 * Replace one node's whole config, by id.
		 *
		 * The edit dialog needs this: it commits a DRAFT on Done, for whichever
		 * node it was opened on — which is not necessarily the selected one.
		 *
		 * @param {string} id     The node id.
		 * @param {object} config The new config object.
		 * @return {void}
		 */
		setNodeConfigById(id, config) {
			if (!this.pushUndo()) {
				return
			}

			this.flow.nodes = this.nodes.map((node) => (
				node.id === id ? { ...node, config: { ...config } } : node
			))
			this.dirty = true
			this.checkResult = null
		},

		/**
		 * Whether a node carries a position of its own.
		 *
		 * BOTH SPELLINGS, because both are in circulation: the server stores
		 * `position: {x, y}` and the editor writes flat `x`/`y` in memory.
		 * Checking only one would read a positioned flow as unpositioned and
		 * rearrange it — the opposite of what load() wants.
		 *
		 * A node AT the origin counts as positioned. (0, 0) is a legitimate
		 * place to put something, and treating it as "no position" would keep
		 * relaying out a flow whose author had parked a node there.
		 *
		 * @param {object} node The node.
		 * @return {boolean} True when the node says where it goes.
		 */
		hasPosition(node) {
			return Number.isFinite(Number(node?.x))
				|| Number.isFinite(Number(node?.position?.x))
		},

		/**
		 * Lay the nodes out left-to-right by how the flow actually runs.
		 *
		 * The pass lives in `flowGraphLayout.js`: longest-path layering seeded
		 * from the start nodes with back edges dropped (a loop terminates the
		 * layering instead of stretching it), one barycenter ordering pass per
		 * column so parallel branches stay apart, fixed grid spacing.
		 * Unreachable nodes go one column past everything — never at the
		 * origin, where they would hide under the entry points. Coordinates
		 * and NOTHING else change, which is what makes this safe to press on
		 * a working flow.
		 *
		 * This is the EDIT — the toolbar button. It asks `pushUndo()` first,
		 * so a locked graph refuses it, and it marks the document dirty so the
		 * new layout is offered for saving. The rendering fallback that may
		 * not do either of those things is `applyRenderLayout()`.
		 *
		 * @return {void}
		 */
		autoSort() {
			if (!this.pushUndo()) {
				return
			}

			if (!this.nodes.length) {
				return
			}

			this.flow.nodes = layoutFlowNodes(this.nodes, this.canvasEdges, this.startNodeIds)
			this.dirty = true
		},

		/**
		 * Give every node a usable position, without editing anything.
		 *
		 * A rendering fallback, not an edit: no undo entry, no dirty flag, no
		 * lifecycle refusal — which is what lets it run on a PUBLISHED flow,
		 * where `autoSort()` correctly refuses. The definition is untouched as
		 * far as anyone else can tell: the coordinates live on the in-memory
		 * copy `open()` already deep-cloned, nothing here persists them, and a
		 * viewer who only looks never writes them anywhere. On a DRAFT the
		 * author goes on to edit, the next save carries them along the
		 * existing save path — which is the point: a once-opened flow becomes
		 * stable instead of being relaid on every visit.
		 *
		 * Two cases, decided by the positions actually present:
		 *
		 * - No usable positions at all — none, or every positioned node piled
		 *   on one identical point — lays the WHOLE graph out fresh.
		 * - An arranged graph with some position-less nodes keeps every placed
		 *   node exactly where its author put it and slots only the loose ones
		 *   beneath, in run order.
		 *
		 * @return {boolean} Whether any node was given a position.
		 */
		applyRenderLayout() {
			const nodes = this.flow.nodes || []
			if (!nodes.length) {
				return false
			}

			if (needsFullLayout(nodes)) {
				this.flow.nodes = layoutFlowNodes(nodes, this.canvasEdges, this.startNodeIds)
				return true
			}

			if (nodes.some((node) => !this.hasPosition(node))) {
				this.flow.nodes = placeLooseNodes(nodes, this.canvasEdges, this.startNodeIds)
				return true
			}

			return false
		},

		/**
		 * Ask the engine whether the CANVAS is runnable, without saving it.
		 *
		 * `POST /api/flow/validate` preflights the document against the live
		 * node registry — the same check a save performs, minus the write. A
		 * refusal (400) still carries the preflight's own report, so it is kept
		 * as the result rather than treated as a transport failure: "this flow
		 * is not runnable, and here is why" is the answer the button asked for.
		 *
		 * @return {Promise<object|null>} `{valid, blocking, warnings, message?}`, or null.
		 */
		async check() {
			this.checking = true
			this.error = null
			try {
				const response = await axios.post(
					generateUrl('/apps/openregister/api/flow/validate'),
					{ flow: this.flowForRun },
				)
				this.checkResult = response.data || null
				return this.checkResult
			} catch (error) {
				const report = error?.response?.data
				if (report && typeof report === 'object' && 'valid' in report) {
					this.checkResult = report
					return this.checkResult
				}

				// eslint-disable-next-line no-console
				console.error('cn-flow: could not check the flow', error)
				this.error = error
				return null
			} finally {
				this.checking = false
			}
		},

		/**
		 * Create or update the flow.
		 *
		 * `owner` and `organisation` are never sent: the server stamps them, and
		 * a client-supplied owner would let an author mint a flow that RUNS as
		 * somebody else.
		 *
		 * 🔴 A VERSION SNAPSHOT IS NEVER SAVED, AND THE UI IS NOT THE GUARD.
		 *
		 * While `viewingVersion` is set the canvas holds a HISTORIC graph and
		 * `flow.id` still names the live flow, so the PUT below would overwrite
		 * the current flow with an old graph. The disabled Save button is not
		 * protection: this method is also reached by the keyboard shortcut, and
		 * the server only refuses a PUBLISHED flow — a flow that was published,
		 * run, and then drafted again has version rows to inspect AND accepts
		 * writes, which is exactly the combination that loses the graph.
		 *
		 * Returning null rather than throwing: nothing has gone wrong, the save
		 * simply does not apply to what is on screen.
		 *
		 * @return {Promise<object|null>} The stored flow, or null on failure.
		 */
		async save() {
			if (this.viewingVersion !== null) {
				return null
			}

			this.saving = true
			this.error = null
			try {
				const payload = { ...this.flow }
				delete payload.owner
				delete payload.organisation

				const isNew = !payload.id
				const response = isNew
					? await axios.post(generateUrl('/apps/openregister/api/flows'), payload)
					: await axios.put(generateUrl(`/apps/openregister/api/flows/${payload.id}`), payload)

				const saved = response.data || null
				if (saved?.id) {
					this.flow = { ...this.flow, id: saved.id }
				}

				await this.load({ app: this.flow.app })
				this.dirty = false
				return saved
			} catch (error) {
				// A 409 is not a failure to save; it is the flow's STATE saying
				// this edit needs a draft first. Kept as the server's fields so
				// the sidebar can offer "create a draft version" rather than a
				// generic error nobody can act on.
				if (error?.response?.status === 409) {
					this.lifecycleRefusal = {
						reason: error.response.data?.reason || 'version-immutable',
						lifecycleStatus: error.response.data?.lifecycleStatus || null,
					}
					return null
				}

				// eslint-disable-next-line no-console
				console.error('cn-flow: could not save the flow', error)
				this.error = error
				return null
			} finally {
				this.saving = false
			}
		},

		/**
		 * Publish the open flow's draft, so it backs new runs.
		 *
		 * Runs already in flight are untouched: they are pinned to the version
		 * they started on, which is the whole point of publishing being an
		 * event rather than a save.
		 *
		 * @param {string|null} bump The semver component to advance ('major' |
		 *                          'minor' | 'patch'), or null to let the server
		 *                          decide from what actually changed.
		 * @return {Promise<object|null>} The published version, or null.
		 */
		async publish(bump = null) {
			return await this.transition('publish', (bump ? { bump } : null))
		},

		/**
		 * Ask what publishing would be called, without publishing.
		 *
		 * A GET that changes nothing. The author is asking before they commit
		 * to an answer, so a refusal here would make it impossible to find out
		 * that a change is breaking without first asserting that it is not.
		 *
		 * @return {Promise<object|null>} The preview, or null when it could not
		 *                                be fetched.
		 */
		async previewPublish() {
			if (!this.flow.id) {
				return null
			}

			this.previewingPublish = true
			this.versionBumpRefusal = null

			try {
				const response = await axios.get(generateUrl(`/apps/openregister/api/flows/${this.flow.id}/version-preview`))
				this.publishPreview = response.data || null
				return this.publishPreview
			} catch (error) {
				// 🔑 A PREVIEW THAT FAILS MUST NOT BLOCK THE PUBLISH. It is a
				// courtesy, and an instance whose route is older than this
				// build would otherwise lose the ability to publish at all.
				// eslint-disable-next-line no-console
				console.error('cn-flow: could not preview the next version', error)
				this.publishPreview = null
				return null
			} finally {
				this.previewingPublish = false
			}
		},

		/**
		 * Create a draft version from the published one.
		 *
		 * The published version keeps serving until the draft is published, so
		 * editing a live process never takes it offline.
		 *
		 * @return {Promise<object|null>} The new draft version, or null.
		 */
		async createDraft() {
			return await this.transition('draft')
		},

		/**
		 * Retire the published version, so the flow backs no new runs.
		 *
		 * @return {Promise<object|null>} The deprecated version, or null.
		 */
		async deprecate() {
			return await this.transition('deprecate')
		},

		/**
		 * POST one lifecycle transition and reload the flow it changed.
		 *
		 * 🔑 THE RELOAD IS NOT OPTIONAL. A transition changes `version` and
		 * `lifecycleStatus`, and those decide whether the canvas is editable.
		 * Leaving the client's copy stale would show an author an editable
		 * canvas over a version the server will refuse to write.
		 *
		 * @param {string} action `publish`, `draft` or `deprecate`.
		 * @param {object|null} body What to send with it, such as `{bump}`.
		 * @return {Promise<object|null>} The resulting version, or null.
		 */
		async transition(action, body = null) {
			if (!this.flow.id) {
				this.error = new Error('Save the flow before changing its lifecycle.')
				return null
			}

			this.transitioning = true
			this.lifecycleRefusal = null
			this.versionBumpRefusal = null

			try {
				// 🔑 NO BODY MEANS NO SECOND ARGUMENT. `draft` and `deprecate`
				// take nothing, and sending them an empty object changes the
				// call every caller and test has ever seen for the sake of a
				// parameter only `publish` uses.
				const url = generateUrl(`/apps/openregister/api/flows/${this.flow.id}/${action}`)
				const response = body
					? await axios.post(url, body)
					: await axios.post(url)

				// RELOAD THE LIST FIRST. `open()` reads from `this.flows`, not
				// from the server, so opening without reloading would restore
				// the pre-transition copy and show a published flow as still a
				// draft — with an editable canvas over it.
				const id = this.flow.id
				const app = this.flow.app
				await this.load({ app })
				this.open(id, app)
				await this.loadVersions()

				return response.data || null
			} catch (error) {
				if (error?.response?.status === 409) {
					// 🔑 TWO DIFFERENT 409s. `version-bump-refused` means the
					// flow is fine and the REQUEST was wrong — the author asked
					// to call a removal minor — and its remedy is to publish it
					// as major, not to create a draft. Reading both as a
					// lifecycle refusal offers the wrong button.
					if (error.response.data?.kind === 'version-bump-refused') {
						this.versionBumpRefusal = error.response.data?.error || null
						return null
					}

					this.lifecycleRefusal = {
						reason: error.response.data?.reason || null,
						lifecycleStatus: error.response.data?.lifecycleStatus || null,
					}
					return null
				}

				// eslint-disable-next-line no-console
				console.error(`cn-flow: could not ${action} the flow`, error)
				this.error = error
				return null
			} finally {
				this.transitioning = false
			}
		},

		/**
		 * Load every version of the open flow, newest first.
		 *
		 * @return {Promise<Array>} The versions.
		 */
		async loadVersions() {
			if (!this.flow.id) {
				this.versions = []
				return this.versions
			}

			try {
				const response = await axios.get(generateUrl(`/apps/openregister/api/flows/${this.flow.id}/versions`))
				this.versions = response.data?.results || []
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('cn-flow: could not read the flow versions', error)
				this.versions = []
			}

			return this.versions
		},

		/**
		 * Delete a flow, then reload the list.
		 *
		 * The reload scope matters: the editor deletes the OPEN flow, so
		 * `this.flow.app` is the right scope there — but an index page deletes
		 * a ROW while no flow is open, and `this.flow.app` is then blank,
		 * which reloads EVERY app's flows into a list that was scoped to one.
		 * Callers that know their scope pass it; the default keeps the
		 * editor's behaviour.
		 *
		 * @param {string} id The flow id or uuid.
		 * @param {object} [options] Reload options.
		 * @param {string|null} [options.app] The app scope to reload with;
		 *   when omitted, the open flow's app is used.
		 * @return {Promise<void>} Resolves when deleted and reloaded.
		 */
		async remove(id, options = {}) {
			await axios.delete(generateUrl(`/apps/openregister/api/flows/${id}`))
			const app = ('app' in options) ? options.app : this.flow.app
			await this.load({ app })
		},

		/**
		 * Copy a stored flow under a new name, then reload the list.
		 *
		 * Reads the STORED flow (`GET /api/flows/{id}`) rather than trusting a
		 * list row, and re-posts its editable fields. Identity and lifecycle
		 * are deliberately not copied: `FlowController::create()`'s allowlist
		 * would refuse them anyway (owner is stamped from the session,
		 * lifecycle starts fresh), but stripping them here documents that a
		 * copy is a NEW flow — same graph, nobody's history.
		 *
		 * @param {string} id The flow to copy.
		 * @param {string} name The copy's name.
		 * @param {object} [options] Reload options.
		 * @param {string|null} [options.app] The app scope to reload with.
		 * @return {Promise<object>} The created flow.
		 */
		async duplicate(id, name, options = {}) {
			const response = await axios.get(generateUrl(`/apps/openregister/api/flows/${id}`))
			const {
				id: _id,
				uuid: _uuid,
				owner: _owner,
				organisation: _organisation,
				created: _created,
				updated: _updated,
				lifecycleStatus: _lifecycleStatus,
				version: _version,
				...editable
			} = response.data || {}
			const createResponse = await axios.post(
				generateUrl('/apps/openregister/api/flows'),
				{ ...editable, name },
			)
			const app = ('app' in options) ? options.app : this.flow.app
			await this.load({ app })
			return createResponse.data
		},

		/**
		 * Run the stored flow, immediately or queued, as the flow itself says.
		 *
		 * A flow must be SAVED before it runs. The engine walks the stored
		 * document, so running unsaved canvas state would report on a graph that
		 * is not the one on screen.
		 *
		 * 🔴 `executionMode` DECIDES, AND IT WAS NEVER SENT. This POST carried
		 * only the subject, so the endpoint fell back to its async default and
		 * every press of Run produced a row reading `queued` — including for a
		 * flow the author had explicitly set to run synchronously. The endpoint
		 * has accepted `sync` all along; nothing was asking for it.
		 *
		 * A sync run answers with the FINISHED run rather than a queued one,
		 * which is what a person standing in front of the button expects. An
		 * async flow still queues, because that is what async means.
		 *
		 * @param {object} subject `{uuid, register, schema}` of the subject.
		 * @return {Promise<object|null>} The run, finished or queued.
		 */
		async run(subject = {}) {
			if (!this.flow.id) {
				this.error = new Error('Save the flow before running it.')
				return null
			}

			this.running = true
			try {
				const response = await axios.post(
					generateUrl(`/apps/openregister/api/flows/${this.flow.id}/run`),
					{ subject, sync: this.runsSynchronously },
				)
				await this.loadRuns(this.flow.id)

				// The POST answers with the created run, uuid included. That
				// uuid used to be discarded, which is why pressing Run left a
				// perfectly still canvas: nothing ever looked at the run again
				// until the user opened the Runs tab. Watching it is what puts
				// the run ON the canvas.
				const run = response.data || null
				if (run?.uuid) {
					this.watchRun(run.uuid)
				}

				return run
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('cn-flow: could not run the flow', error)
				this.error = error
				return null
			} finally {
				this.running = false
			}
		},

		/**
		 * Watch one run: poll it while its status is non-terminal.
		 *
		 * Polls `GET /apps/openregister/api/flow-runs/{uuid}` on an interval
		 * (default 3s, floored at 2s), stops by itself on a terminal status,
		 * on a re-watch, and on teardown. Pauses while the document is hidden
		 * and resumes with one immediate fetch when it becomes visible — the
		 * behaviour CnFlowRunsWidget already ships.
		 *
		 * Polling, not push, because that is what exists: the flow-run API is
		 * REST only, and the engine persists the log once per worker pass
		 * anyway, so a push channel would today deliver the same bursts.
		 *
		 * @param {string} uuid                The run to watch.
		 * @param {object} options             Watch options.
		 * @param {number} options.intervalMs  Poll interval in ms; floored at 2000.
		 * @return {void}
		 */
		watchRun(uuid, { intervalMs = WATCH_INTERVAL_DEFAULT_MS } = {}) {
			this.stopWatching()

			if (!uuid) {
				return
			}

			this.watchedRunUuid = uuid
			this.watchedRun = null
			this.watchedSteps = []
			this.watchedNewFrom = 0
			this.watching = true

			const requested = Number(intervalMs)
			this._watchIntervalMs = Math.max(
				WATCH_INTERVAL_FLOOR_MS,
				Number.isFinite(requested) ? requested : WATCH_INTERVAL_DEFAULT_MS,
			)

			// Bound so removeEventListener gets the SAME function back — an
			// inline arrow here could be added but never removed.
			this._watchOnVisibility = () => this.onWatchVisibilityChange()
			document.addEventListener('visibilitychange', this._watchOnVisibility)

			this.fetchWatchedRun()
			this.startWatchTimer()
		},

		/**
		 * Start (or restart) the poll interval — unless the watch is over or
		 * the tab is hidden, in which case the visibility handler restarts it.
		 *
		 * @return {void}
		 */
		startWatchTimer() {
			this.clearWatchTimer()

			if (this.watching !== true || document.hidden === true) {
				return
			}

			this._watchTimer = setInterval(() => {
				this.fetchWatchedRun()
			}, this._watchIntervalMs)
		},

		/**
		 * Clear the poll interval. The watch itself stays what it was.
		 *
		 * @return {void}
		 */
		clearWatchTimer() {
			if (this._watchTimer !== undefined && this._watchTimer !== null) {
				clearInterval(this._watchTimer)
				this._watchTimer = null
			}
		},

		/**
		 * One poll of the watched run.
		 *
		 * Appends the log entries beyond what `watchedSteps` already holds —
		 * the log only grows within a run, so the index diff is exact and an
		 * already-exposed step is never replayed. Stops the watch when the
		 * status turns terminal, keeping the final run and steps for the
		 * canvas's last paint.
		 *
		 * @return {Promise<void>}
		 */
		async fetchWatchedRun() {
			const uuid = this.watchedRunUuid
			if (!uuid) {
				return
			}

			try {
				const response = await axios.get(generateUrl(`/apps/openregister/api/flow-runs/${uuid}`))

				// A re-watch can land while this request is in the air; its
				// answer describes a run nobody is watching any more.
				if (this.watchedRunUuid !== uuid) {
					return
				}

				const run = response.data || null
				this.watchedRun = run

				const log = Array.isArray(run?.log) ? run.log : []
				this.watchedNewFrom = this.watchedSteps.length
				if (log.length > this.watchedSteps.length) {
					this.watchedSteps = [
						...this.watchedSteps,
						...log.slice(this.watchedSteps.length),
					]
				}

				if (run !== null && FLOW_RUN_ACTIVE_STATUSES.includes(run.status) === false) {
					this.stopWatching()

					// The Runs tab lists this run with the status it had when
					// the list was last read — usually `queued`. Refresh so the
					// history agrees with the canvas about how it ended.
					if (this.flow.id) {
						this.loadRuns(this.flow.id)
					}
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('cn-flow: could not read the watched run', error)

				// A missing run will not come back; anything else (a blip, a
				// timeout) is worth the next poll.
				if (error?.response?.status === 404) {
					this.stopWatching()
				}
			}
		},

		/**
		 * Stop polling. The watched run and its steps are KEPT — the canvas's
		 * final paint reads them — only the loop and its listener go.
		 *
		 * @return {void}
		 */
		stopWatching() {
			this.clearWatchTimer()

			if (this._watchOnVisibility !== undefined && this._watchOnVisibility !== null) {
				document.removeEventListener('visibilitychange', this._watchOnVisibility)
				this._watchOnVisibility = null
			}

			this.watching = false
		},

		/**
		 * Pause the poll while the tab is hidden; refetch once and resume when
		 * it comes back, so returning shows current state rather than whatever
		 * was true when the tab was backgrounded.
		 *
		 * @return {void}
		 */
		onWatchVisibilityChange() {
			if (document.hidden === true) {
				this.clearWatchTimer()
				return
			}

			if (this.watching !== true) {
				return
			}

			this.fetchWatchedRun()
			this.startWatchTimer()
		},

		/**
		 * Ask the canvas to replay the inspected run's stored log.
		 *
		 * The steps are already here — `inspectRun()` fetched them — so this
		 * only raises the token the canvas half watches. A live watch is
		 * stopped first: a replay and a live run cannot share the canvas.
		 *
		 * @return {void}
		 */
		requestReplay() {
			if (!this.inspectedRunUuid || this.steps.length === 0) {
				return
			}

			this.stopWatching()
			this.replayUuid = this.inspectedRunUuid
			this.replayToken += 1
		},

		async loadRuns(flowId) {
			try {
				const response = await axios.get(generateUrl('/apps/openregister/api/flow-runs'), {
					params: { flowId, limit: 25 },
				})
				this.runs = response.data?.results || []
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('cn-flow: could not load run history', error)
				this.runs = []
			}
		},

		/**
		 * Load one run's per-node steps.
		 *
		 * This is what the step table exists for — the run's aggregate log
		 * cannot answer "which node produced this" without being walked.
		 *
		 * @param {string} runUuid The run to inspect.
		 * @return {Promise<void>}
		 */
		async inspectRun(runUuid) {
			this.inspectedRunUuid = runUuid
			let version = null
			try {
				const response = await axios.get(generateUrl(`/apps/openregister/api/flow-runs/${runUuid}`))
				// KEPT WHOLE, not picked apart. The record carries the run's
				// status, its error, and the objects it is about, none of which
				// can be recovered from the log or from the capped history list.
				this.runDetail = response.data || null
				this.steps = response.data?.log || []
				// Read from the RUN, not from `this.runs`. A run reached by
				// `?run=` need not be in the loaded page of run history at all —
				// the history is capped at 25 — and the run's own record is the
				// authority on which version of the flow it executed.
				version = response.data?.flowVersion ?? null
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('cn-flow: could not load the run steps', error)
				this.runDetail = null
				this.steps = []
			}

			// BEFORE the objects and tasks: those two only fill panels, while
			// this changes what the canvas is showing, and a reader watching the
			// graph swap after the panels have settled reads it as a glitch.
			await this.showRunVersionGraph(version)

			// Loaded alongside the steps, not on demand behind another click.
			// The steps say what the run DID; these say what it did it TO, and
			// reading one without the other is the gap this exists to close.
			await this.loadRunObjects(runUuid)
			await this.loadRunTasks(runUuid)
		},

		/**
		 * Put the graph a run actually executed onto the canvas.
		 *
		 * The version's stored graph replaces `flow.nodes`/`flow.edges` for as
		 * long as the run is open. The displaced graph is stashed in `liveGraph`
		 * so `closeRun()` can put it back exactly — refetching instead would
		 * lose an author's in-progress layout for no gain.
		 *
		 * 🔴 UNSAVED WORK IS NEVER DISPLACED. `dirty` means the author has edits
		 * on this canvas that exist nowhere else. Swapping the graph out from
		 * under them would destroy those edits with no undo and no warning, to
		 * show a nicety. So a dirty canvas keeps its graph and the run replays
		 * over it, exactly as it did before this existed, and CnFlowDetail says
		 * so on the canvas rather than leaving the reader to guess which graph
		 * they are looking at.
		 *
		 * A version that cannot be read — pruned, or never recorded for a run
		 * from before versioning — is recorded in `runGraphNotice` and
		 * the replay falls back to the live graph. That fallback is the OLD
		 * behaviour, so nothing regresses; it just stops being silent.
		 *
		 * @param {number|string|null} version The run's `flowVersion`.
		 * @return {Promise<void>}
		 */
		async showRunVersionGraph(version) {
			this.runGraphNotice = null

			const number = Number(version)
			if (!this.flow.id || !Number.isInteger(number) || number <= 0) {
				return
			}

			if (this.dirty === true) {
				this.runGraphNotice = { version: number, reason: 'unsaved-edits' }
				return
			}

			try {
				const response = await axios.get(generateUrl(`/apps/openregister/api/flows/${this.flow.id}/versions/${number}`))
				const graph = response.data?.graph
				if (!graph || Array.isArray(graph.nodes) === false) {
					this.runGraphNotice = { version: number, reason: 'unreadable' }
					return
				}

				// Stashed ONCE. Moving between two runs of the same flow calls
				// this twice, and overwriting the stash on the second call would
				// store the FIRST run's snapshot as the live graph — after
				// which closing the run restores a version instead of the flow.
				if (this.liveGraph === null) {
					this.liveGraph = {
						nodes: this.flow.nodes,
						edges: this.flow.edges,
					}
				}

				this.flow = {
					...this.flow,
					// Deep-copied for the same reason `open()` deep-copies the
					// list row: the canvas mutates node positions, and a stored
					// version is not the place to discover that.
					nodes: JSON.parse(JSON.stringify(graph.nodes)),
					edges: Array.isArray(graph.edges) ? JSON.parse(JSON.stringify(graph.edges)) : [],
				}
				this.viewingVersion = number

				// A stored version carries whatever coordinates it was saved
				// with, and a flow imported from `x-openregister-flows` was
				// saved with none — so the same pile `open()` guards against
				// arrives here by a different route.
				this.applyRenderLayout()
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('cn-flow: could not read the graph this run executed', error)
				this.runGraphNotice = { version: number, reason: 'unreadable' }
			}
		},

		/**
		 * Stop inspecting a run and put the live graph back.
		 *
		 * The one way out, so the restore cannot be forgotten. Setting
		 * `inspectedRunUuid = null` by hand would leave a historic graph on a
		 * canvas that no longer says it is historic — and, because the lock
		 * lifts with `viewingVersion`, an editable one.
		 *
		 * @return {void}
		 */
		closeRun() {
			this.inspectedRunUuid = null
			this.runDetail = null
			// The other half of run view. Leaving it set would keep every
			// surface in run view after the reader asked to go back to the flow,
			// and the canvas would keep saying the run was still opening.
			this.openingRunUuid = null
			this.steps = []
			this.runObjects = []
			this.runTasks = []
			this.replayUuid = null
			this.runGraphNotice = null

			if (this.liveGraph !== null) {
				this.flow = {
					...this.flow,
					nodes: this.liveGraph.nodes,
					edges: this.liveGraph.edges,
				}
				this.liveGraph = null
			}

			this.viewingVersion = null
		},

		/**
		 * Load the tasks one run raised.
		 *
		 * 🔴 AN EMPTY UUID IS NOT A QUERY, AND THE SERVER WILL NOT SAVE US.
		 * `?runUuid=` is treated as ABSENT, and an absent anchor means the
		 * endpoint answers with the caller's ordinary inbox. Sending one would
		 * put somebody's own task list under a run's Tasks tab, so the guard is
		 * here rather than a hope that an empty answer comes back.
		 *
		 * 🔴 THE RESPONSE IS CHECKED AGAINST THE RUN, NOT TRUSTED. The anchor
		 * is merged (openregister `801cc02b`), but this is a LIBRARY: a
		 * consuming app can be running an openregister from before it, where an
		 * unknown query parameter is silently dropped rather than rejected. That
		 * server returns a perfectly valid 200 full of the reader's own tasks,
		 * and rendering it would be a disclosure dressed as a feature with
		 * nothing failing anywhere. Rows that do not name this run are dropped;
		 * against an old server that empties the tab, which is the honest
		 * answer.
		 *
		 * `scope` is deliberately NOT sent. The anchor overrides it, so passing
		 * one would be a parameter that reads as though it narrowed something.
		 *
		 * @param {string} runUuid The run to read.
		 * @return {Promise<void>}
		 */
		async loadRunTasks(runUuid) {
			if (!runUuid) {
				this.runTasks = []
				return
			}

			try {
				const response = await axios.get(generateUrl('/apps/openregister/api/flow-tasks'), {
					params: { runUuid },
				})

				const rows = response.data?.results || []
				this.runTasks = rows.filter((task) => String(task?.runUuid || '') === String(runUuid))
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('cn-flow: could not load the tasks this run raised', error)
				this.runTasks = []
			}
		},

		/**
		 * Load the objects one run touched, grouped by the node that touched them.
		 *
		 * A run's own record names only the object that TRIGGERED it, so
		 * everything it went on to create or change is answerable only from the
		 * attribution stamped on the audit trail. This is that read.
		 *
		 * Failure is quiet and empties the list rather than leaving the previous
		 * run's objects on screen — stale rows here would be read as "this run
		 * touched those", which is worse than showing nothing.
		 *
		 * @param {string} runUuid The run to read.
		 * @return {Promise<void>}
		 */
		async loadRunObjects(runUuid) {
			try {
				const response = await axios.get(generateUrl(`/apps/openregister/api/flow-runs/${runUuid}/objects`))
				this.runObjects = response.data?.nodes || []
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('cn-flow: could not load the objects this run touched', error)
				this.runObjects = []
			}
		},
	},
})
